import * as z from 'zod';

export const CreateCategoryRequest = z.object({
  category: z
    .object({
      _id: z.string().describe('Category ID.').max(100).optional(),
      displayName: z
        .string()
        .describe('Display name shown for the category.')
        .max(100)
        .min(2),
      parentCategoryId: z
        .string()
        .describe('ID of the parent category.\n\nDefault: Account category ID.')
        .max(100)
        .optional()
        .nullable(),
      sortOrder: z
        .number()
        .describe(
          'Display order among sibling categories under the same parent. Lower values appear first. Supports decimals to insert between existing positions without renumbering.'
        )
        .optional()
        .nullable(),
      publishStatus: z
        .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the category was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the category was updated.')
        .optional()
        .nullable(),
    })
    .describe('Category to create.'),
});
export const CreateCategoryResponse = z.object({
  _id: z.string().describe('Category ID.').max(100).optional(),
  displayName: z
    .string()
    .describe('Display name shown for the category.')
    .max(100)
    .min(2)
    .optional()
    .nullable(),
  parentCategoryId: z
    .string()
    .describe('ID of the parent category.\n\nDefault: Account category ID.')
    .max(100)
    .optional()
    .nullable(),
  sortOrder: z
    .number()
    .describe(
      'Display order among sibling categories under the same parent. Lower values appear first. Supports decimals to insert between existing positions without renumbering.'
    )
    .optional()
    .nullable(),
  publishStatus: z
    .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
    .describe(
      'Publication status of the category. Controls whether the category is visible to child accounts.'
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the category was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the category was updated.')
    .optional()
    .nullable(),
});
export const DeleteCategoryRequest = z.object({
  categoryId: z.string().describe('Category ID.').max(100),
});
export const DeleteCategoryResponse = z.object({});
export const UpdateCategoryRequest = z.object({
  _id: z.string().describe('Category ID.').max(100),
  category: z
    .object({
      _id: z.string().describe('Category ID.').max(100).optional(),
      displayName: z
        .string()
        .describe('Display name shown for the category.')
        .max(100)
        .min(2)
        .optional()
        .nullable(),
      parentCategoryId: z
        .string()
        .describe('ID of the parent category.\n\nDefault: Account category ID.')
        .max(100)
        .optional()
        .nullable(),
      sortOrder: z
        .number()
        .describe(
          'Display order among sibling categories under the same parent. Lower values appear first. Supports decimals to insert between existing positions without renumbering.'
        )
        .optional()
        .nullable(),
      publishStatus: z
        .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the category was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the category was updated.')
        .optional()
        .nullable(),
    })
    .describe('Category to update.'),
});
export const UpdateCategoryResponse = z.object({
  _id: z.string().describe('Category ID.').max(100).optional(),
  displayName: z
    .string()
    .describe('Display name shown for the category.')
    .max(100)
    .min(2)
    .optional()
    .nullable(),
  parentCategoryId: z
    .string()
    .describe('ID of the parent category.\n\nDefault: Account category ID.')
    .max(100)
    .optional()
    .nullable(),
  sortOrder: z
    .number()
    .describe(
      'Display order among sibling categories under the same parent. Lower values appear first. Supports decimals to insert between existing positions without renumbering.'
    )
    .optional()
    .nullable(),
  publishStatus: z
    .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
    .describe(
      'Publication status of the category. Controls whether the category is visible to child accounts.'
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the category was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the category was updated.')
    .optional()
    .nullable(),
});
export const GetCategoryRequest = z.object({
  categoryId: z.string().describe('Category ID.').max(100),
  options: z
    .object({
      levels: z
        .number()
        .int()
        .describe(
          'Number of subcategory levels to include in the response. For example, `levels=1` returns the category with its immediate children, `levels=2` includes grandchildren as well. When not specified, returns only the requested category without subcategories.'
        )
        .optional()
        .nullable(),
      publishStatus: z
        .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
        .optional(),
    })
    .optional(),
});
export const GetCategoryResponse = (() => {
  let comWixMediaEnterprise_public_mediaV1EnterpriseCategoryTreeSchema: z.ZodType<any> =
    z.object({
      category: z
        .object({
          _id: z.string().describe('Category ID.').max(100).optional(),
          displayName: z
            .string()
            .describe('Display name shown for the category.')
            .max(100)
            .min(2)
            .optional()
            .nullable(),
          parentCategoryId: z
            .string()
            .describe(
              'ID of the parent category.\n\nDefault: Account category ID.'
            )
            .max(100)
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .describe(
              'Display order among sibling categories under the same parent. Lower values appear first. Supports decimals to insert between existing positions without renumbering.'
            )
            .optional()
            .nullable(),
          publishStatus: z
            .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
            .describe(
              'Publication status of the category. Controls whether the category is visible to child accounts.'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the category was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the category was updated.')
            .optional()
            .nullable(),
        })
        .describe('Category information.')
        .optional(),
      subCategories: z
        .array(
          z.lazy(
            () =>
              comWixMediaEnterprise_public_mediaV1EnterpriseCategoryTreeSchema
          )
        )
        .max(100)
        .optional(),
    });
  return comWixMediaEnterprise_public_mediaV1EnterpriseCategoryTreeSchema;
})();
export const EnterpriseOnboardingRequest = z.object({
  accountId: z
    .string()
    .describe(
      'Account ID of the organization. Used as the organization category ID.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      accountName: z
        .string()
        .describe(
          'Account name of the organization. Used as the organization category name.'
        )
        .max(50)
        .optional(),
    })
    .optional(),
});
export const EnterpriseOnboardingResponse = z.object({
  category: z
    .object({
      _id: z.string().describe('Category ID.').max(100).optional(),
      displayName: z
        .string()
        .describe('Display name shown for the category.')
        .max(100)
        .min(2)
        .optional()
        .nullable(),
      parentCategoryId: z
        .string()
        .describe('ID of the parent category.\n\nDefault: Account category ID.')
        .max(100)
        .optional()
        .nullable(),
      sortOrder: z
        .number()
        .describe(
          'Display order among sibling categories under the same parent. Lower values appear first. Supports decimals to insert between existing positions without renumbering.'
        )
        .optional()
        .nullable(),
      publishStatus: z
        .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
        .describe(
          'Publication status of the category. Controls whether the category is visible to child accounts.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the category was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the category was updated.')
        .optional()
        .nullable(),
    })
    .describe('Created enterprise category.')
    .optional(),
});
export const GetMediaManagerCategoriesRequest = z.object({});
export const GetMediaManagerCategoriesResponse = (() => {
  let comWixMediaEnterprise_public_mediaV1EnterpriseCategoryTreeSchema: z.ZodType<any> =
    z.object({
      category: z
        .object({
          _id: z.string().describe('Category ID.').max(100).optional(),
          displayName: z
            .string()
            .describe('Display name shown for the category.')
            .max(100)
            .min(2)
            .optional()
            .nullable(),
          parentCategoryId: z
            .string()
            .describe(
              'ID of the parent category.\n\nDefault: Account category ID.'
            )
            .max(100)
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .describe(
              'Display order among sibling categories under the same parent. Lower values appear first. Supports decimals to insert between existing positions without renumbering.'
            )
            .optional()
            .nullable(),
          publishStatus: z
            .enum(['UNDEFINED', 'UNPUBLISHED', 'PUBLISHED'])
            .describe(
              'Publication status of the category. Controls whether the category is visible to child accounts.'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the category was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the category was updated.')
            .optional()
            .nullable(),
        })
        .describe('Category information.')
        .optional(),
      subCategories: z
        .array(
          z.lazy(
            () =>
              comWixMediaEnterprise_public_mediaV1EnterpriseCategoryTreeSchema
          )
        )
        .max(100)
        .optional(),
    });
  return z.object({
    category: comWixMediaEnterprise_public_mediaV1EnterpriseCategoryTreeSchema
      .describe('Category tree for the media manager.')
      .optional(),
  });
})();
